'use client'

import React from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { Icon } from '@/ui/atoms/Icon'
import { EventDate } from '@/ui/atoms/EventDate'
import { getDateFormat } from '@/utils/getDateFormat'
import { formatDateRange } from '@/lib/events'

export type EventHeaderProps = {
  title: string
  startDate?: string | null
  endDate?: string | null
  location?: string | null
  organizer?: string | null
  /** When set, the organiser name links here (internal path or external URL). */
  organizerHref?: string | null
  showEventDate?: boolean
  titleAs?: 'h2' | 'h3'
  titleClassName?: string
  /**
   * Card grid mode (Events page, 2026-09-15): fixed row heights so every card's title,
   * organiser line and date/location line sit at the same height — title reserves 3 lines,
   * organiser is one truncated line, dates use the short "15–16 Sep 2026" form on one line.
   */
  aligned?: boolean
  /** Small element rendered above the title (Events cards: the event-type chip). */
  eyebrow?: React.ReactNode
}

export const EventHeader: React.FC<EventHeaderProps> = ({
  title,
  startDate,
  endDate,
  location,
  organizer,
  organizerHref,
  showEventDate = true,
  titleAs = 'h3',
  titleClassName,
  aligned = false,
  eyebrow,
}) => {
  const TitleTag = titleAs
  const external = Boolean(organizerHref && /^https?:\/\//.test(organizerHref))

  const organizerNode = organizer && (
    <p
      className={classNames(
        'text-body-S-regular text-text-subtle',
        aligned && 'truncate leading-[1.5] min-h-[1.5em]',
      )}
    >
      by{' '}
      {organizerHref ? (
        external ? (
          <a
            href={organizerHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-text-brand hover:underline"
          >
            {organizer}
          </a>
        ) : (
          <Link href={organizerHref} onClick={(e) => e.stopPropagation()} className="text-text-brand hover:underline">
            {organizer}
          </Link>
        )
      ) : (
        organizer
      )}
    </p>
  )

  return (
    <div className="flex items-start gap-4">
      {showEventDate && startDate && <EventDate date={startDate} />}
      <div className="flex-grow space-y-1 min-w-0">
        {eyebrow && <div className="flex mb-2">{eyebrow}</div>}
        <TitleTag
          className={
            titleClassName ||
            classNames(
              titleAs === 'h3'
                ? 'text-title-S-medium lg:text-title-M-medium font-semibold text-gray-900'
                : 'text-headline-S-semibold text-text-base',
              aligned && 'leading-[1.35] min-h-[2.7em] line-clamp-2',
            )
          }
          title={aligned ? title : undefined}
        >
          {title}
        </TitleTag>
        {aligned ? organizerNode || <p className="min-h-[1.5em]" aria-hidden /> : organizerNode}
        <div
          className={classNames(
            'flex text-sm text-gray-500',
            aligned
              ? 'flex-row items-center gap-2 whitespace-nowrap overflow-hidden'
              : endDate
                ? 'flex-col gap-2 lg:flex-row lg:items-center'
                : 'flex-row items-center gap-2',
          )}
        >
          {startDate && aligned && (
            <span className="text-label-XS-regular leading-none shrink-0">{formatDateRange(startDate, endDate)}</span>
          )}
          {startDate && !aligned && (
            <div className="flex items-center gap-2">
              <span className={'text-label-XS-regular leading-none'}>
                {getDateFormat(startDate, 'full')}
              </span>
              {endDate && <span className={'text-label-XS-regular leading-none'}>-</span>}
              {endDate && (
                <span className={'text-label-XS-regular leading-none'}>
                  {getDateFormat(endDate, 'full')}
                </span>
              )}
            </div>
          )}

          {startDate && location && (
            <div className={classNames('size-1 rounded-full bg-gray-500 shrink-0', !aligned && endDate && 'hidden lg:block')} />
          )}

          {location && (
            <div className="flex items-center gap-1 min-w-0">
              <Icon name={'ri-map-pin-line'} className={'!size-3 shrink-0'} />
              <span className={classNames('text-label-XS-semibold leading-none text-text-subtle', aligned && 'truncate')}>
                {location}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
